import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetPipedriveLeadConfigQuery,
	useSavePipedriveLeadConfigMutation,
	useGetPipedriveOwnersQuery,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { PipedriveLeadConfig } from "../schema/pipedrive";

const sanitizeStr = (val: string | null | undefined): string | null => {
	if (!val || val === "undefined" || val === "null") {
		return null;
	}

	return val;
};

const DEFAULT_CURRENCIES: { id: string; title: string }[] = [
	{ id: "USD", title: "USD - US Dollar" },
	{ id: "EUR", title: "EUR - Euro" },
	{ id: "GBP", title: "GBP - British Pound" },
	{ id: "CHF", title: "CHF - Swiss Franc" },
	{ id: "CAD", title: "CAD - Canadian Dollar" },
	{ id: "AUD", title: "AUD - Australian Dollar" },
];

const parseLabelIds = (raw: string): string[] => {
	return raw
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0)
		.slice(0, 20);
};

export const PipedriveLeadConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.pipedrive.leadConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: leadConfig, isLoading: isLoadingConfig } = useGetPipedriveLeadConfigQuery();
	const { data: ownersData } = useGetPipedriveOwnersQuery();
	const [saveLeadConfig, { isLoading: isSaving }] = useSavePipedriveLeadConfigMutation();

	const [config, setConfig] = useState<PipedriveLeadConfig>({
		defaultCurrency: null,
		defaultValue: null,
		defaultLabelIds: null,
		defaultOwnerId: null,
		expectedCloseDateOffset: null,
	});
	const [labelIdsRaw, setLabelIdsRaw] = useState<string>("");

	useEffect(() => {
		if (leadConfig) {
			setConfig({
				defaultCurrency: sanitizeStr(leadConfig.defaultCurrency),
				defaultValue: leadConfig.defaultValue ?? null,
				defaultLabelIds: leadConfig.defaultLabelIds ?? null,
				defaultOwnerId: sanitizeStr(leadConfig.defaultOwnerId),
				expectedCloseDateOffset: leadConfig.expectedCloseDateOffset ?? null,
			});
			setLabelIdsRaw((leadConfig.defaultLabelIds ?? []).join(", "));
		}
	}, [leadConfig]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			ownersData?.owners?.map((o) => ({
				id: o.id,
				title: o.name ? `${o.name} (${o.email})` : o.email,
			})) || [],
		[ownersData]
	);

	const labelCount = useMemo(() => parseLabelIds(labelIdsRaw).length, [labelIdsRaw]);

	const handleSave = async () => {
		try {
			const labelIds = parseLabelIds(labelIdsRaw);
			const payload: PipedriveLeadConfig = {
				defaultCurrency: sanitizeStr(config.defaultCurrency),
				defaultValue: config.defaultValue ?? null,
				defaultLabelIds: labelIds.length > 0 ? labelIds : null,
				defaultOwnerId: sanitizeStr(config.defaultOwnerId),
				expectedCloseDateOffset: config.expectedCloseDateOffset ?? null,
			};

			await saveLeadConfig(payload).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-1">{ts("description")}</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Dropdown
					data={DEFAULT_CURRENCIES}
					defaultValue={
						config.defaultCurrency
							? DEFAULT_CURRENCIES.find((c) => c.id === config.defaultCurrency)
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({ ...prev, defaultCurrency: value.id as string }));
					}}
					label={ts("defaultCurrency")}
				/>

				<InputField
					handleChange={(e) => {
						const parsed = parseFloat(e.target.value);

						setConfig((prev) => ({
							...prev,
							defaultValue: Number.isFinite(parsed) && parsed >= 0 ? parsed : null,
						}));
					}}
					information={ts("defaultValueHelp")}
					label={ts("defaultValue")}
					min={0}
					name="defaultValue"
					type="number"
					value={config.defaultValue ?? ""}
				/>

				<Dropdown
					data={ownerOptions}
					defaultValue={
						config.defaultOwnerId
							? ownerOptions.find((o) => String(o.id) === String(config.defaultOwnerId))
							: undefined
					}
					floating={true}
					handleSelect={(value) => {
						setConfig((prev) => ({ ...prev, defaultOwnerId: value.id as string }));
					}}
					label={ts("defaultOwner")}
				/>

				<InputField
					handleChange={(e) => {
						const parsed = parseInt(e.target.value, 10);

						setConfig((prev) => ({
							...prev,
							expectedCloseDateOffset:
								Number.isFinite(parsed) && parsed >= 1 && parsed <= 365 ? parsed : null,
						}));
					}}
					information={ts("closeDateOffsetHelp")}
					label={ts("closeDateOffsetDays")}
					max={365}
					min={1}
					name="expectedCloseDateOffset"
					type="number"
					value={config.expectedCloseDateOffset ?? ""}
				/>

				<div className="md:col-span-2 flex flex-col gap-1">
					<InputField
						handleChange={(e) => setLabelIdsRaw(e.target.value)}
						information={ts("defaultLabelIdsHelp")}
						label={ts("defaultLabelIds")}
						name="defaultLabelIds"
						placeholder="label-id-1, label-id-2"
						value={labelIdsRaw}
					/>
					<span className="text-xs text-text-meta">
						{ts("defaultLabelIdsCount").replace("{{count}}", String(labelCount))}
					</span>
				</div>
			</div>

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
